import { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import Button from 'src/componentes/button';
import Dialog from 'src/componentes/dialog';
import FormularioGlobalRespostaPreview, {
  buildFormularioGlobalSections,
  getFormularioGlobalAnswerValidationError,
} from 'src/componentes/formulario-global/FormularioGlobalRespostaPreview';
import { randomizeFormularioGlobalSections } from 'src/componentes/formulario-global/formularioGlobalQuestionOrder';
import { getFormularioGlobalBranchingSections } from 'src/componentes/formulario-global/formularioGlobalBranching';
import guid from 'src/utils/guid';
import { FORMATO_EXIBICAO_FORMULARIO_GLOBAL } from 'src/utils/constants';

const FormularioGlobalPreviewDialog = ({ formulario, ...rest }) => {
  const intl = useIntl();
  const dialogRef = useRef();
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isWelcomeStep, setIsWelcomeStep] = useState(true);
  const [randomizationKey] = useState(guid);
  const allSections = useMemo(() => {
    const orderedSections = buildFormularioGlobalSections(formulario, intl);

    return formulario?.randomizarPerguntas
      ? randomizeFormularioGlobalSections(orderedSections, randomizationKey)
      : orderedSections;
  }, [formulario, intl, randomizationKey]);
  const sections = useMemo(
    () =>
      getFormularioGlobalBranchingSections(
        allSections,
        answers,
        formulario?.randomizarPerguntas
      ),
    [allSections, answers, formulario?.randomizarPerguntas]
  );
  const steps = sections.flatMap((section) =>
    section.perguntas.map((question) => ({ section, question }))
  );
  const currentIndex = Math.min(questionIndex, Math.max(steps.length - 1, 0));
  const currentStep = steps[currentIndex];
  const formatoExibicao = formulario?.formatoExibicao;
  const singleQuestionMode =
    Number(formatoExibicao?.id ?? formatoExibicao?.Id ?? formatoExibicao) ===
      FORMATO_EXIBICAO_FORMULARIO_GLOBAL.PERGUNTA_POR_VEZ && steps.length > 0;
  const visibleSections = !singleQuestionMode
    ? sections
    : isWelcomeStep
    ? []
    : [{ ...currentStep.section, perguntas: [currentStep.question] }];

  const handleClose = () => {
    dialogRef.current?.close();
  };

  const handleAnswerChange = (questionKey, patch) => {
    setErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };
      delete nextErrors[questionKey];
      return nextErrors;
    });
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionKey]: {
        ...(currentAnswers[questionKey] || {}),
        ...patch,
      },
    }));
  };

  function handleNext() {
    if (isWelcomeStep) {
      setIsWelcomeStep(false);
      return;
    }

    const question = currentStep.question;
    const questionKey = question._formularioGlobalPreviewKey;
    const errorId = getFormularioGlobalAnswerValidationError(
      question,
      answers[questionKey] || {}
    );
    setErrors(
      errorId ? { [questionKey]: intl.formatMessage({ id: errorId }) } : {}
    );
    if (errorId) return;

    if (currentIndex === steps.length - 1) {
      handleClose();
    } else {
      setQuestionIndex(currentIndex + 1);
    }
  }

  return (
    <Dialog
      actions={[
        <Button color="secondary" key="fechar" onClick={handleClose}>
          {intl.formatMessage({ id: 'label.fechar' })}
        </Button>,
      ]}
      ignoreResourceTitle
      maxHeight="90%"
      maxWidth="90%"
      minWidth="80%"
      onRequestClose={handleClose}
      ref={dialogRef}
      title={intl.formatMessage({ id: 'visualizar' })}
      {...rest}
    >
      <FormularioGlobalRespostaPreview
        answers={answers}
        errors={errors}
        embedded
        formulario={formulario}
        onAnswerChange={handleAnswerChange}
        sections={visibleSections}
        singleQuestionMode={singleQuestionMode}
        showDescription={!singleQuestionMode || isWelcomeStep}
        footer={
          singleQuestionMode ? (
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
              <div>
                {!isWelcomeStep && currentIndex > 0 && (
                  <Button
                    type="button"
                    color="outline-primary"
                    onClick={() => setQuestionIndex(currentIndex - 1)}
                  >
                    {intl.formatMessage({ id: 'label.voltar' })}
                  </Button>
                )}
              </div>
              {!isWelcomeStep && (
                <span className="tw:text-sm tw:text-muted-foreground">
                  {intl.formatMessage(
                    { id: 'labelPerguntaAtualDeTotal' },
                    { atual: currentIndex + 1, total: steps.length }
                  )}
                </span>
              )}
              <Button type="button" onClick={handleNext}>
                {intl.formatMessage({
                  id:
                    !isWelcomeStep && currentIndex === steps.length - 1
                      ? 'label.fechar'
                      : 'labelAvancar',
                })}
              </Button>
            </div>
          ) : null
        }
        titleFallback={intl.formatMessage({ id: 'labelFormularioSemTitulo' })}
      />
    </Dialog>
  );
};

export default FormularioGlobalPreviewDialog;
